import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { find, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../../../common/ConstantsLagoon';
import { capitalizeFirstLetter } from '../../../../../../common/Utils/capitalizeFirstLetter';
import { useLanguageTranslations } from '../../../../../../common/Widget/SmartChoiceWidget/utils';
import { getLanguage } from '../../../../../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useLagoon } from '../../../../../../hooks/useLagoon';
import {
  getEthnicityDesc,
  getRaceDesc,
} from '../../../../../../utils/providerDetails.utils';
import { LayoutGroupStyled } from '../../../CompareSmartChoice/CompareSmartChoice.styled';
import { isAttributeChecked } from '../../../CompareSmartChoice/utils';
import {
  ATTRIBUTE_STATUS_TEXT,
  CHECKBOX_ITEM_NAMES,
  NULL_ATTRIBUTE_STATE,
} from '../../../MapView/CompareDrawer/utility/compareDrawerConstants';
import { getStatusText } from '../../../MapView/ShareDrawer/utils';
import {
  IconWithText,
  PrintViewDivider,
  PrintViewProviderDetailsXSText,
  Section,
} from './PrintExpandedCardStyle.styled';

export const PrintExpandedPersonalFitCard = ({
  selectedAttributes,
  selectedProviders,
  preferencesData,
  isSmartChoice,
}) => {
  const { t } = useTranslation();
  const lagoonData = useLagoon('ui-messaging')();
  const language = getLanguage()?.code || 'en';
  const languageMap = useLanguageTranslations(language);

  const isProviderEthnicityEmpty = (item) =>
    isEmpty(item?.providerEthnicityExpress);
  const ethnicity_not_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE
      .COMPARE_ETHINICITY_INFO_NOT_AVAILABLE,
  })?.message;
  const providerEthnicityText = (item) =>
    isProviderEthnicityEmpty(item)
      ? ethnicity_not_available
      : item?.providerEthnicityExpress;
  const eapAcceptedText = (item) =>
    t(
      getStatusText(
        item?.isEap,
        ATTRIBUTE_STATUS_TEXT.EAP_ACCEPTED.TRUE,
        ATTRIBUTE_STATUS_TEXT.EAP_ACCEPTED.FALSE,
        NULL_ATTRIBUTE_STATE.EAP_ACCEPTED
      )
    );

  const preferredLanguage = preferencesData?.languagesSpoken ?? '';
  const preferredGender = preferencesData?.providerGender ?? '';
  const preferredBoardCert = preferencesData?.providerBoardCertStatus ?? '';

  const isAttributeEmpty = (item) => isEmpty(item?.practiceLimitations);
  const practiceLimitationsText = (item) => {
    if (isAttributeEmpty(item))
      return t(NULL_ATTRIBUTE_STATE.PRACTICE_LIMITATIONS);
    if (item?.practiceLimitations?.startAge !== '0')
      return t(ATTRIBUTE_STATUS_TEXT.PRACTICE_LIMITATIONS.TRUE);
    return t(ATTRIBUTE_STATUS_TEXT.PRACTICE_LIMITATIONS.FALSE);
  };

  const isServicesOfferedEmpty = (item) => isEmpty(item?.servicesOffered);
  function formatAndTranslateScreamingSnakeCase(value: string) {
    return t(
      capitalizeFirstLetter(value.replaceAll('_', ' ').toLocaleLowerCase())
    );
  }
  const servicesOfferedText = (item) =>
    isServicesOfferedEmpty(item)
      ? t(NULL_ATTRIBUTE_STATE.SERVICES_OFFERED)
      : item?.servicesOffered
          .map((value) => formatAndTranslateScreamingSnakeCase(value))
          .join(', ');

  const spoken_languages_unknown = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_LANGUAGE_UNKNOWN,
  })?.message;
  const identified_as_male = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_MALE,
  })?.message;
  const identified_as_female = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_FEMALE,
  })?.message;
  const not_certified = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NOT_BOARD_CERTIFIED,
  })?.message;
  const rating_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_CONSUMER_RATING_INFO_UNAVAILABLE,
  })?.message;
  const provider_gender_unknown = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_GENDER_UNKNOWN,
  })?.message;
  const provider_board_certified = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_BOARD_CERTIFIED_IN,
  })?.message;

  const providerLanguages = (item) => item?.languagesSpoken ?? [];
  const providerLanguagesByOffice = (item) =>
    item?.languagesSpokenByOffice ?? [];

  const allProviderLanguages = (provider) => {
    const combinedLanguages = [
      ...providerLanguages(provider),
      ...providerLanguagesByOffice(provider).map((item) => item?.toLowerCase()),
    ];

    const translatedLanguages = combinedLanguages
      .map((lang) => languageMap[lang])
      .filter((item) => item);

    return [...new Set(translatedLanguages)];
  };

  return (
    <Section>
      {selectedAttributes?.find(
        (record) =>
          record.name === 'languageSpoken' || record?.name === 'languagesSpoken'
      )?.isChecked &&
        ((isSmartChoice && preferredLanguage !== '') || !isSmartChoice) && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {selectedProviders?.map((item, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  <PrintViewProviderDetailsXSText>
                    {allProviderLanguages(item)?.join(', ') ||
                      spoken_languages_unknown}{' '}
                  </PrintViewProviderDetailsXSText>
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {selectedProviders?.map((_, index) => (
                <PrintViewDivider key={index} orientation="horizontal" />
              ))}
            </LayoutGroupStyled>
          </>
        )}

      {selectedAttributes?.find((record) => record.name === 'gender')
        ?.isChecked &&
        ((isSmartChoice && preferredGender !== '') || !isSmartChoice) && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {selectedProviders?.map((item, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  <PrintViewProviderDetailsXSText
                    css={{ textTransform: 'capitalize' }}
                    data-testid="provider-gender"
                  >
                    {item?.gender === 'male' && identified_as_male}
                    {item?.gender === 'female' && identified_as_female}
                    {item?.gender !== 'male' &&
                      item?.gender !== 'female' &&
                      item?.gender}
                    {!item?.gender && provider_gender_unknown}
                  </PrintViewProviderDetailsXSText>
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {selectedProviders?.map((_, index) => (
                <PrintViewDivider key={index} orientation="horizontal" />
              ))}
            </LayoutGroupStyled>
          </>
        )}

      {selectedAttributes?.find(
        (record) => record.name === CHECKBOX_ITEM_NAMES.PROVIDER_RACE
      )?.isChecked && (
        <>
          <LayoutGroupStyled cssProps={{ fullWidthDivs: true }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                <PrintViewProviderDetailsXSText data-testid="provider-race">
                  {getRaceDesc(
                    item.providerRace,
                    t,
                    'COMPARE.RACE_NOT_AVAILABLE'
                  )}
                </PrintViewProviderDetailsXSText>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            cssProps={{ minHeightAuto: true, fullWidthDivs: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}

      {selectedAttributes?.find(
        (record) => record.name === CHECKBOX_ITEM_NAMES.ETHNICITY
      )?.isChecked && (
        <>
          <LayoutGroupStyled cssProps={{ fullWidthDivs: true }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                <PrintViewProviderDetailsXSText data-testid="provider-ethinicity">
                  {getEthnicityDesc(item?.providerEthnicity, t, true)}
                </PrintViewProviderDetailsXSText>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            cssProps={{ minHeightAuto: true, fullWidthDivs: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}

      {selectedAttributes.find((record) => record.name === 'boardCertified')
        ?.isChecked &&
        preferredBoardCert === 'y' && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {selectedProviders?.map((item, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  <PrintViewProviderDetailsXSText
                    css={{ textTransform: 'capitalize' }}
                    data-testid="provider-board-certifications"
                  >
                    {item?.boardCertifications?.length
                      ? provider_board_certified?.replace(
                          '{{providerBoardCertifications}}',
                          item?.boardCertifications
                            ?.map((cert) => cert?.value?.toLowerCase())
                            ?.join(', ')
                        )
                      : not_certified}
                  </PrintViewProviderDetailsXSText>
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {selectedProviders?.map((_, index) => (
                <PrintViewDivider key={index} orientation="horizontal" />
              ))}
            </LayoutGroupStyled>
          </>
        )}
      {selectedAttributes.find((record) => record.name === 'reviews')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                {item?.healthGradeRating ? (
                  <div
                    style={{
                      marginTop: isAttributeChecked(selectedAttributes, [
                        'languageSpoken',
                        'languagesSpoken',
                        'gender',
                      ])
                        ? '8px'
                        : '24px',
                    }}
                  >
                    <IconWithText>
                      <IconSymbol color="#F5B700" icon="star" size={20} />
                      <PrintViewProviderDetailsXSText>
                        {`${item?.healthGradeRating} (${item?.surveyCount})`}
                      </PrintViewProviderDetailsXSText>
                    </IconWithText>
                  </div>
                ) : (
                  <PrintViewProviderDetailsXSText>
                    {rating_unavailable}
                  </PrintViewProviderDetailsXSText>
                )}
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%' } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}
      {/* Provider Ethnicity */}
      {selectedAttributes.find(
        (record) => record.name === 'providerEthnicityExpress'
      )?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                <PrintViewProviderDetailsXSText data-testid="provider-ethnicity">
                  {providerEthnicityText(item)}
                </PrintViewProviderDetailsXSText>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%' } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}

      {/* EAP ACCEPTED */}
      {selectedAttributes.find((record) => record.name === 'eapAccepted')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                <PrintViewProviderDetailsXSText>
                  {eapAcceptedText(item)}
                </PrintViewProviderDetailsXSText>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%' } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}
      {/* Practice Limitations */}
      {selectedAttributes.find(
        (record) => record.name === 'practiceLimitations'
      )?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                {item?.practiceLimitations?.startAge ? (
                  <Flex alignItems="center" data-testid="practice-limitations">
                    <IconSymbol
                      color="$core.color.brand.100"
                      icon="back_hand"
                      size={20}
                    />
                    <PrintViewProviderDetailsXSText css={{ marginLeft: '8px' }}>
                      {item?.practiceLimitations?.startAge === '0'
                        ? practiceLimitationsText(item)
                        : `${practiceLimitationsText(item)}: ${
                            item?.practiceLimitations?.startAge
                          }+`}
                    </PrintViewProviderDetailsXSText>
                  </Flex>
                ) : (
                  <PrintViewProviderDetailsXSText data-testid="practice-limitations">
                    {practiceLimitationsText(item)}
                  </PrintViewProviderDetailsXSText>
                )}
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%' } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}

      {/* Serives Offered */}
      {selectedAttributes.find((record) => record.name === 'servicesOffered')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                <PrintViewProviderDetailsXSText data-testid="services-offered">
                  {t(servicesOfferedText(item))}
                </PrintViewProviderDetailsXSText>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%' } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}
    </Section>
  );
};
